import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { markSynced, readCache, writeCache } from '../utils/localCache'
import { setTableIndex } from '../utils/tableLabel'
import {
  subscribeCampaigns,
  subscribeCategories,
  subscribeHalls,
  subscribeProducts,
  subscribeTables,
} from '../services/products'
import { useAuth } from './AuthContext'
import { requestTranslation } from '../services/menuTranslations'
import type { Campaign, Category, Hall, Product, Table } from '../types'

interface CatalogValue {
  categories: Category[]
  products: Product[]
  /** Active products only, in menu order, what the till screen shows. */
  activeProducts: Product[]
  activeCategories: Category[]
  /** Tables available to open a ticket against. */
  activeTables: Table[]
  /** Sections of the room, in their settings order, for grouping tables. */
  halls: Hall[]
  /** Kampanya kuralları (açık ve kapalı), Ayarlar sırasıyla. */
  campaigns: Campaign[]
  productsByCategory: (categoryId: string) => Product[]
  loading: boolean
  error: string | null
}

const CatalogContext = createContext<CatalogValue | undefined>(undefined)

export function CatalogProvider({ children }: { children: ReactNode }) {
  const { tenantId, isAuthenticated, canSettle, hasFeature } = useAuth()
  const [categories, setCategories] = useState<Category[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [tables, setTables] = useState<Table[]>([])
  const [halls, setHalls] = useState<Hall[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // QR menü çevirisi: menüyü düzenleyebilen biri bağlıyken, ad ya da açıklama
  // değişince birkaç saniye sonra çeviri fonksiyonu çağrılıyor. Hangi ekrandan
  // (Menü, CSV, kurulum) değiştiği fark etmiyor. Fonksiyon yalnızca değişen
  // kalemleri çeviriyor; değişen yoksa hiçbir şey yapmıyor.
  const translateWanted = Boolean(tenantId && isAuthenticated && canSettle && hasFeature('qr'))
  const lastSig = useRef<string | null>(null)
  const menuSig = useMemo(
    () =>
      JSON.stringify([
        categories.filter((c) => c.active).map((c) => [c.id, c.name]),
        products.filter((p) => p.active).map((p) => [p.id, p.name, p.description ?? '']),
      ]),
    [categories, products],
  )
  useEffect(() => {
    if (!translateWanted || loading || !tenantId) return
    if (lastSig.current === menuSig) return
    const timer = window.setTimeout(() => {
      lastSig.current = menuSig
      void requestTranslation(tenantId).then((r) => {
        if (r.error) console.warn('[çeviri]', r.error)
      })
    }, 8000)
    return () => window.clearTimeout(timer)
  }, [translateWanted, loading, tenantId, menuSig])

  // The listeners stay open for the whole session. That is what warms the
  // offline cache: the full catalogue must already be local before the signal
  // drops at the venue.
  useEffect(() => {
    if (!tenantId || !isAuthenticated) {
      setLoading(false)
      return
    }

    // Önce cihazdaki son kopya (28 Eki 2026): yenileyince ve internetsizken
    // ekran boş kalmasın, sunucunun tazesi arkadan yerine otursun.
    const cats = readCache<Category[]>(tenantId, 'categories')
    const prods = readCache<Product[]>(tenantId, 'products')
    const cachedTables = readCache<Table[]>(tenantId, 'tables')
    const cachedHalls = readCache<Hall[]>(tenantId, 'halls')
    const cachedCampaigns = readCache<Campaign[]>(tenantId, 'campaigns')
    let haveCopy = Boolean(cats && prods)
    setCategories(cats ?? [])
    setProducts(prods ?? [])
    setTables(cachedTables ?? [])
    setHalls(cachedHalls ?? [])
    setCampaigns(cachedCampaigns ?? [])
    setError(null)
    setLoading(!haveCopy)

    let gotCategories = false
    let gotProducts = false
    const settle = () => {
      if (gotCategories && gotProducts) {
        haveCopy = true
        setLoading(false)
        markSynced()
      }
    }

    const onError = (err: Error) => {
      console.error('[catalog]', err)
      // Kopya ekrandaysa (çevrimdışı) hata ekranı açılmıyor, kopya kalıyor.
      if (!haveCopy) setError('Menü yüklenemedi.')
      setLoading(false)
    }

    const unsubCategories = subscribeCategories(tenantId, (items) => {
      setCategories(items)
      writeCache(tenantId, 'categories', items)
      gotCategories = true
      settle()
    }, onError)

    const unsubProducts = subscribeProducts(tenantId, (items) => {
      setProducts(items)
      writeCache(tenantId, 'products', items)
      gotProducts = true
      settle()
    }, onError)

    const unsubTables = subscribeTables(tenantId, (items) => {
      setTables(items)
      writeCache(tenantId, 'tables', items)
    }, onError)
    // Salonlar yalnızca gruplamak için; okunamazsa masalar düz liste kalır.
    const unsubHalls = subscribeHalls(tenantId, (items) => {
      setHalls(items)
      writeCache(tenantId, 'halls', items)
    }, (err) => console.error('[catalog] salonlar', err))
    // Kampanyalar okunamazsa kasada öneri çıkmaz, satış etkilenmez.
    const unsubCampaigns = subscribeCampaigns(tenantId, (items) => {
      setCampaigns(items)
      writeCache(tenantId, 'campaigns', items)
    }, (err) => console.error('[catalog] kampanyalar', err))

    return () => {
      unsubCategories()
      unsubProducts()
      unsubTables()
      unsubHalls()
      unsubCampaigns()
    }
  }, [tenantId, isAuthenticated])

  /**
   * Masalar her ekranda aynı sırada (kullanıcı, 22 Eki 2026): önce salon
   * (Ayarlar'daki salon sırası, salonsuzlar en sonda), sonra salon içinde
   * ada göre. Sayılara duyarlı: "Masa 2" "Masa 10"dan önce. Eskiden eklenme
   * sırasıydı, sonradan eklenen masa en sona düşüyordu.
   */
  // Görünen masa adı (utils/tableLabel displayTable) bu listeye göre: fiş de
  // kullandığı için modüle bildiriliyor.
  useEffect(() => setTableIndex(tables), [tables])

  const sortedTables = useMemo(() => {
    const rank = new Map(halls.map((h, i) => [h.id, i]))
    const hallRank = (t: Table) => (t.hallId && rank.has(t.hallId) ? rank.get(t.hallId)! : halls.length)
    return [...tables].sort(
      (a, b) =>
        hallRank(a) - hallRank(b) ||
        a.name.localeCompare(b.name, 'tr', { numeric: true, sensitivity: 'base' }),
    )
  }, [tables, halls])

  const value = useMemo<CatalogValue>(() => {
    const activeProducts = products.filter((p) => p.active)
    const activeCategories = categories.filter((c) => c.active)
    return {
      categories,
      products,
      activeProducts,
      activeCategories,
      activeTables: sortedTables.filter((t) => t.active),
      halls,
      campaigns,
      productsByCategory: (categoryId: string) =>
        activeProducts.filter((p) => p.categoryId === categoryId),
      loading,
      error,
    }
  }, [categories, products, sortedTables, halls, campaigns, loading, error])

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
}

export function useCatalog() {
  const ctx = useContext(CatalogContext)
  if (!ctx) throw new Error('useCatalog must be used within a CatalogProvider')
  return ctx
}
